import { useCallback, useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/glossyApi";
import { toast } from "sonner";
import { RefreshCw, X, Zap } from "lucide-react";
import { cycleIndexFor, editionLabel, editionStartIso, upcomingEditions } from "@/lib/issue";

// The Placement Board — 16 fixed advert spaces per edition. The editor pins EXACTLY which
// member's advert sits in which space for the fortnight. Auto-fill places once, then it's locked.
export default function PlacementBoard() {
  const [issue, setIssue] = useState(editionStartIso(cycleIndexFor()));
  const [board, setBoard] = useState(null);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback((iss) => {
    client.get("/admin/placement-board", { params: { issue: iss } }).then((r) => { setBoard(r.data); setPicks({}); }).catch(() => {});
  }, []);
  useEffect(() => { load(issue); }, [issue, load]);

  const assign = async (slot) => {
    const memberId = picks[slot];
    if (!memberId) return toast.error("Pick a member for this space first.");
    setBusy(slot);
    try {
      const r = await client.post("/admin/placements/assign", { issue, slot, member_id: memberId });
      toast.success(`${r.data.business_name} pinned — fixed for the full edition.`);
      load(issue);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(null);
  };

  const clear = async (slot) => {
    setBusy(slot);
    try { await client.post("/admin/placements/clear", { issue, slot }); toast.success("Space emptied — it collapses on the site."); load(issue); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(null);
  };

  const autofill = async () => {
    setBusy("autofill");
    try {
      const r = await client.post("/admin/placements/autofill", { issue });
      const n = (r.data.filled || []).length;
      toast.success(n ? `${n} space${n === 1 ? "" : "s"} filled — placements are now fixed for the edition.` : "Nothing to fill — every unplaced advert is already in, or all spaces are taken.");
      load(issue);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(null);
  };

  if (!board) return null;
  const taken = (board.spaces || []).filter((s) => s.holder).length;
  const unplaced = (board.candidates || []).filter((c) => !["live", "scheduled"].includes(c.run_status));

  return (
    <div data-testid="placement-board">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-ink/15 pb-2 mb-1">
        <p className="overline text-gloss-silver">The Placement Board — {board.spaces.length} fixed spaces</p>
        <div className="flex items-center gap-2">
          <select value={issue} onChange={(e) => setIssue(e.target.value)} className="border border-ink/25 bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-wider focus:outline-none focus:border-gloss-gold" data-testid="pb-issue">
            {[cycleIndexFor(), ...upcomingEditions(6)].map((idx) => (
              <option key={idx} value={editionStartIso(idx)}>{editionLabel(idx)}{idx === cycleIndexFor() ? " (current)" : ""}</option>
            ))}
          </select>
          <button onClick={autofill} disabled={busy === "autofill"} className="overline flex items-center gap-2 bg-ink text-cream px-4 py-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="pb-autofill">
            {busy === "autofill" ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={13} />} Auto-fill empty spaces
          </button>
        </div>
      </div>
      <p className="font-sans text-xs text-ink/50 mb-4">
        Every advert holds ONE exact space for the full two-week edition — nothing rotates, nothing moves.
        Pin a member into any space, or Auto-fill the empties once and they stay put. Empty spaces simply collapse on the site.
        {" "}<b>{taken}/{board.spaces.length}</b> spaces taken · {unplaced.length} advert{unplaced.length === 1 ? "" : "s"} ready but unplaced.
      </p>
      <div className="border border-ink/15 bg-white">
        {board.spaces.map((s) => (
          <div key={s.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 border-b border-ink/10 last:border-0" data-testid={`pb-slot-${s.id}`}>
            <div className="sm:w-[300px] shrink-0">
              <p className="font-serif text-base leading-tight">{s.label}</p>
              <p className="font-mono text-[10px] text-ink/40 mt-0.5">{s.where}</p>
            </div>
            <div className="flex-1 min-w-0">
              {s.holder ? (
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`overline px-2 py-0.5 shrink-0 ${s.holder.status === "live" ? "bg-ink text-cream" : "border border-gloss-gold text-ink"}`}>{s.holder.status}</span>
                  <span className="font-serif text-sm truncate" data-testid={`pb-holder-${s.id}`}>{s.holder.business_name}</span>
                  {s.holder.headline && <span className="font-sans text-xs text-ink/45 truncate hidden md:inline">— “{s.holder.headline}”</span>}
                </div>
              ) : (
                <span className="font-sans text-xs text-ink/40">Empty — collapses on the site</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select value={picks[s.id] || ""} onChange={(e) => setPicks((p) => ({ ...p, [s.id]: e.target.value }))} className="border border-ink/25 bg-white px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider focus:outline-none focus:border-gloss-gold max-w-[200px]" data-testid={`pb-pick-${s.id}`}>
                <option value="">Pin a member…</option>
                {(board.candidates || []).map((c) => (
                  <option key={c.member_id} value={c.member_id} disabled={c.is_cover_star && issue === board.current_issue}>
                    {c.business_name}{c.is_cover_star ? " — Cover Star" : c.run_status === "live" || c.run_status === "scheduled" ? ` — placed (${c.run_status})` : ""}
                  </option>
                ))}
              </select>
              <button onClick={() => assign(s.id)} disabled={busy === s.id || !picks[s.id]} className="overline border border-ink px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid={`pb-assign-${s.id}`}>Pin</button>
              {s.holder && (
                <button onClick={() => clear(s.id)} disabled={busy === s.id} title="Empty this space" className="p-1.5 text-ink/40 hover:text-red-500 transition-colors" data-testid={`pb-clear-${s.id}`}><X size={14} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
